<template>
  <div id="podcast-faqs">
    <h3>Podcasts: Why Summit?</h3>
    <div>
      <p>
        As a national leader in providing high quality live workshops for over a decade, Summit understands that your time and money are extremely valuable -- that you don't want to settle for Online Continuing Education that is poor quality, poorly delivered, or won't be accepted by state and national licensing boards.
      </p>
      <p>
        With that as our foundation, we are proud to offer Online Continuing Education courses that we believe set a new standard of high quality. Leveraging our exceptional teaching faculty that
        delivers thousands of live workshops each year (with an <strong>average customer satisfaction rating of 4.7 out of 5.0</strong>), we have now invested in creating the same
        exceptional Online course experience.
      </p>
      <p>We deliver podcasts that are both high quality and easy to access from our website and multiple podcast platforms:</p>
      <p><strong>We invest in high quality audio production</strong></p>
      <ul>
        <li>Excellent sound, using professional grade microphones </li>
        <li>Edited audio content to focus on highest learning content </li>
      </ul>

      <p><strong>Our Online platform is state of the art</strong></p>
      <ul>
        <li>High quality media player and downloadable resources for each podcast </li>
        <li>You can stop, start, & pick up again at your convenience (up to 30 days) </li>
        <li>Podcasts are also available on several other platforms </li>
        <li>We are mobile friendly, and also provide live telephone technical support, Monday - Friday 7AM to 6PM CST </li>
      </ul>
    </div>

    <h3>Podcasts: What to Expect</h3>
    <div>
      <p>We want to make sure that you are 100% comfortable with what you are purchasing, so this is a rundown of the experience you should expect.</p>
      <p>Podcasts are available for listening prior to purchase through the Summit course page as well as via the following podcast platforms:  </p>

      <l-podcast-social-icons /> <br />

      <p>Once Purchased, listeners will have access to Summit’s media player. Here’s what you can expect from our media player: </p>
      <ul>
        <li>Listen to high quality audio of your instructor while also accessing course resources </li>
        <li>Compatibility with both Mac and PC </li>
        <li>Mobile compatibility so you can watch on your phone or tablet </li>
      </ul>

      <p>We have also built in multiple helpful links within the media player so you can:  </p>
      <ul>
        <li>Download podcast Resources  </li>
        <li>Get technical support  </li>
        <li>Access FAQs to common questions  </li>
        <li>Launch your post-test and obtain your Certificate of Completion  </li>
      </ul>

      <h4>Media Player Details </h4>
      <p>This is what the media player will look like when you first launch the Podcast: </p>
      <img
        src="//summitproedu.imgix.net/redesign-assets/pages/course/podcastPlayerImage.png"
        alt="Podcast Player Sample"
        class="mx-auto"
      />

      <p><strong>Icons at the bottom</strong></p>
      <p>You will notice 4 Icons on the bottom of the player, this is what these tabs do:</p>
      <ul>
        <li><strong>Course Info:</strong> Helpful details regarding the course, anything you need before starting, and how the audio player works </li>
        <li><strong>Resources:</strong> This is where you can see the course outline and download your manual </li>
        <li><strong>FAQs:</strong> Answers to the most common questions to enhance your listening </li>
        <li><strong>Test & Certificate:</strong> Access the post-course test and Certificate of completion after you finish listening </li>
      </ul>

      <p><strong>Links in the top right hand corner</strong></p>
      <p>There are also 3 links in the upper right hand corner:</p>
      <ul>
        <li>Support - Gives you the number to the technical support team for issues with the media player</li>
        <li>Account - Takes you to your online profile, and is also where you log out of the media player</li>
        <li>Logout - Takes you back to the Summit Professional Education website</li>
      </ul>
    </div> <!-- section #1 -->

    <h3>Continuing Education Credit FAQs</h3>
    <h5>Am I able to get a certificate for listening to a podcast without completing the evaluation and post-test? </h5>
    <div>
      <p>
        No, listening to a podcast only meets a portion of the satisfactory completion requirements. You must purchase the course or add it to your subscription and log into your Summit account to access the evaluation and post-test. Please review the Satisfactory Completion Requirements under the ‘CE Credits’ tab for more details.
      </p>
    </div>
    <h5>How do I know if the Podcast offers CE credit for my profession and state? </h5>
    <div>
      <p>You can view the accreditation availability for each profession and state on the Product page of any course in the CE Credit tab (middle of page, far right tab). </p>
      <p>If you have further questions regarding accreditation, please contact Summit Customer Service M-F from 7 a.m. to 6 p.m. CST at (800) 433-9570 or email customerservice@summit-education.com. </p>
    </div>
    <h5>Why don’t all Podcasts offer accreditation for my profession and state?</h5>
    <div>
      <p>The accreditation of podcasts varies by state and profession, and some licensing boards have longer review timelines or differing accreditation criteria than others. </p>
    </div>
    <h5>How many credits do I receive for a Podcast?</h5>
    <div>
      <p>
        Currently, all of our Podcasts are 1 hour in length. You can view the accreditation availability for each profession and state on the Product page (middle of page, far right tab of "CE Credit") where you can see the exact number of credit hours that may be offered for each profession and state as well as view satisfactory completion requirements.
      </p>
      <p>If you have further questions regarding CE credits, please contact Summit Customer Service M-F from 7 a.m. to 6 p.m. CST at (800) 433-9570 or email customerservice@summit-education.com. </p>
    </div>
    <h5>Does Summit report to CE broker? </h5>
    <div>
      <p>
        Yes! Summit offers CE Broker reporting on behalf of eligible licensees in a selection of states. Information on which licensees are eligible and how to request CE Broker reporting can be found <a
          href="https://support.summit-education.com/hc/en-us/articles/360023278011-Does-Summit-report-to-CE-Broker-"
          target="_blank"
        >here.</a>
      </p>
    </div>

    <h3>Podcast Course Content FAQs</h3>
    <div>
      <p><strong>Is an outline of the podcast content available before or during listening? </strong></p>
      <p>You can see a podcast outline on the Product page under the Outline tab. After purchase, the podcast outline is also provided on the Summit media player to view while listening to the course. </p>

      <p><strong>How do I access the course Resources?</strong></p>
      <p>Once you have purchased the course, there is a “Resources” tab at the bottom of the media player where you can download the resources to save or print for your personal use. </p>

      <p><strong>Where can I find who is teaching the Podcast and his/her professional credentials? </strong></p>
      <p>
        You can see the instructor and their credentials on the product page under the Instructor Info tab.
      </p>

      <p><strong>Where can I find who is teaching the course and his/her professional credentials?</strong></p>
      <p>You can see the instructor and their credentials on the product page under the Instructor Info tab</p>
    </div>

    <h3>Test & Certificate</h3>
    <div>
      <p><strong>Will I need to take a post course test? Why?</strong></p>
      <p>Yes, you are required to successfully pass the post-course test in order to access a certificate of completion. This is required by many licensing boards for online courses. </p>
      <p><strong>What is the post-course test like, what is a passing score, and how many times can I take it? </strong></p>
      <p>
        Post course tests consist of True/False and/or multiple-choice questions. You will be able to retake the post course test as many times as necessary to receive a passing grade. A passing "grade" is 75% or higher for general content, 80% or higher for certain state specific workshops, like ethics, jurisprudence, and laws and rules courses. You will be able to retake the post course test as many times as necessary to receive a passing grade.
      </p>
      <p><strong>When will I receive my credits and certificate? </strong></p>
      <p>Once you complete your course evaluation and post-test in your Summit Account, you will be able to immediately download your certificate or completion and request CE Broker reporting (if applicable). </p>
    </div>

    <h3>Technical FAQs</h3>
    <div>
      <p><strong>What browsers and Operating system do I need to run for this to work properly? </strong></p>
      <p>The following are the supported browsers and Operating Systems for the media player to work properly: </p>
      <ul>
        <li>Internet Explorer 11 or Microsoft Edge 44+ for Windows 10</li>
        <li>Google Chrome 75.0+ for Windows, Mac OSX, or Linux</li>
        <li>Mozilla Firefox 70.0+ for Windows, Mac OSX, or Linux</li>
        <li>Safari 12.0+ for Mac OSX 10.13 and above</li>
      </ul>
      <p><strong>Will the media player work on both a Mac and PC?</strong></p>
      <p>Yes, the online courses will play on both a Mac and PC. The following are the supported browsers and Operating Systems for the media player to work properly:</p>
      <ul>
        <li>Internet Explorer 11 or Microsoft Edge 44+ for Windows 10</li>
        <li>Google Chrome 75.0+ for Windows, Mac OSX, or Linux</li>
        <li>Mozilla Firefox 70.0+ for Windows, Mac OSX, or Linux</li>
        <li>Safari 12.0+ for Mac OSX 10.13 and above</li>
      </ul>
      <p><strong>Can the Podcast be played on my phone?</strong></p>
      <p>Yes! You are able to watch the workshops on your phone. Here are the technical requirements to ensure it works properly:</p>
      <ul>
        <li>Safari on iOS 13+</li>
        <li>Safari on iPadOS 13+</li>
        <li>Chrome on Android 9.0+</li>
        <li>Any running Android 9.0+</li>
      </ul>
      <p>Summit Podcasts are also accessible for listening via the following podcast platforms:  </p>
      <l-podcast-social-icons /> <br />
      <p><strong>Are the videos downloadable or streaming?</strong></p>
      <p>All of the podcasts stream from the Summit media player. They can be downloaded if accessed from other Podcast media platforms such as Spotify, Apple Podcasts, Google Podcasts etc. </p>

      <p><strong>Will I experience any occasional buffering or freezing of the audio? </strong></p>
      <p>
        This rarely happens. If it does, it's typically because of customer internet access quality issues and we will provide troubleshooting tips as well as telephone support from our help desk.
      </p>

      <p><strong>Do you provide technical support?</strong></p>
      <p>Our help desk is available to assist you by phone Monday through Friday, 7AM to 6PM at (800) 433-9570.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PodcaseFaq',
};
</script>
